/**
 * `<PlanSection>` — Phase 3 item 3.2.
 *
 * Two subsections, each hidden in read mode when its underlying array is
 * empty (per LD-6 dynamic-section identification). Follow-Up is NOT here — it
 * closes the note as its own `<FollowUpSection>` (PD-1321 section order):
 *
 *   1. Orders / Service Requests — columns Order / CPT / LOINC / Intent / Priority (mockup `ChartPage.tsx:4267-4318`)
 *      (the "Intent" column header was previously labelled "Status"; it sets
 *      `ServiceRequest.intent`, NOT the FHIR lifecycle `status`, which is pinned
 *      to `active` — see `getOrderPillLabel` for the derived read-only pill.)
 *   2. Medications — columns Medication / Dose / Route / Frequency / Status / Note (`:4320-4375`)
 *
 * Edit mode renders the terminology picker INLINE on each row — `<InlineCodePicker>`
 * for orders' CPT column (eager options from `useProcedureOptions`), a plain
 * `<TextInput>` for medications' name (RxNorm picker is parked until a usable
 * RxNorm ValueSet is loaded in Medplum), and an autosize `<Textarea>` for
 * each follow-up entry. `+ Add X` buttons just append an empty row whose
 * inline picker becomes the editable cell.
 *
 * Status pills surface ALL FHIR enum values (not narrowed) — `serviceRequest.intent`
 * exposes order / plan / proposal; `medication.status` exposes the full 5 FHIR
 * states (`active / completed / intended / stopped / on-hold`); priority exposes
 * the full 4 (`routine / urgent / asap / stat`).
 */
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { IconPlus, IconX } from '@tabler/icons-react'
import { Controller, useFormContext, useWatch, type UseFieldArrayReturn } from 'react-hook-form'
import type {
  MedicationItem,
  ProgressNoteEditFormValues,
  ServiceRequestItem,
} from '../progressNoteEditSchema'
import { useProcedureOptions } from '../useProcedureOptions'
import { InlineCodePicker, type InlineCodeOption } from '../InlineCodePicker'
import { RxNormMedicationPicker } from '../RxNormMedicationPicker'
import { ROUTE_OPTIONS, extractDoseFromRxNorm, extractRouteFromRxNorm } from '../rxnormPrefill'
import { BillingSummary } from '@/components/billing'
import { CPT_SYSTEM, LOINC_SYSTEM } from '../fhirSystems'
import { MONO_FONT, PROGRESS_NOTE_CORAL as CORAL } from '../tokens'
import { DictateFieldMic } from '../dictation/DictateFieldMic'
import { NoteHeading } from './NoteHeading'
import { LOW_CONFIDENCE_REASON } from '../scribeDraftPayload'
import { orderTestsPathFor } from '@/utils/order-tests-path'

// Two curated options (PD-1108): "Suggested" = FHIR `proposal`, "Ready to
// Place" = `order`. Values stay the FHIR `ServiceRequest.intent` code (a legacy
// `plan` still reads fine).
const ORDER_INTENT_OPTIONS = [
  { value: 'proposal', label: 'Suggested' },
  { value: 'order', label: 'Ready to Place' },
] as const

// Read-only pill speaks the same vocabulary as the edit dropdown in every note
// state (PD-1108 QA) — lifecycle is signaled by `getOrderPillColors` only.
function getOrderPillLabel(intent: ServiceRequestItem['intent']): string {
  if (intent === 'order') return 'Ready to Place'
  if (intent === 'plan') return 'Planned'
  return 'Suggested'
}

const ORDER_PRIORITY_OPTIONS = [
  { value: 'routine', label: 'Routine' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'asap', label: 'ASAP' },
  { value: 'stat', label: 'STAT' },
] as const

// Mockup parity — keep the FHIR `MedicationStatement.status` enum on the wire
// but relabel for clarity. All 5 FHIR values are visible (Option C in the
// refactor plan): the doctor picks the clinically-appropriate state without
// any FE-side narrowing.
const MED_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'intended', label: 'Planned' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'on-hold', label: 'On Hold' },
] as const

// Mockup color tokens (from ChartPage.tsx :4308 / :4365)
const STATUS_GREEN = { bg: 'rgba(34,197,94,0.1)', fg: '#16a34a' }
const STATUS_AMBER = { bg: 'rgba(251,191,36,0.15)', fg: '#b45309' }
const STATUS_NEUTRAL = { bg: '#f3f4f6', fg: '#9ca3af' }
const STATUS_BLUE = { bg: 'rgba(59,130,246,0.1)', fg: '#1d4ed8' }
const STATUS_RED = { bg: '#fee2e2', fg: '#dc2626' }

const PRIORITY_COLORS: Record<
  NonNullable<ServiceRequestItem['priority']>,
  { bg: string; fg: string }
> = {
  routine: { bg: '#f3f4f6', fg: '#374151' },
  urgent: { bg: 'rgba(251,191,36,0.18)', fg: '#b45309' },
  asap: { bg: 'rgba(232,98,74,0.12)', fg: CORAL },
  stat: { bg: '#fee2e2', fg: '#dc2626' },
}

/**
 * Colors are the pill's only lifecycle signal (labels are intent-fixed per
 * PD-1108): green means "charted in the locked Progress Note", never "the
 * doctor said they intended to order it but we haven't locked yet".
 */
function getOrderPillColors(
  intent: ServiceRequestItem['intent'],
  docStatus: 'preliminary' | 'final' | undefined,
) {
  if (intent === 'order') return docStatus === 'final' ? STATUS_GREEN : STATUS_BLUE
  if (intent === 'plan') return STATUS_AMBER
  return STATUS_NEUTRAL
}

function medStatusColors(status: MedicationItem['status']) {
  if (status === 'active') return STATUS_BLUE
  if (status === 'stopped' || status === 'on-hold') return STATUS_RED
  if (status === 'completed') return STATUS_GREEN
  return STATUS_NEUTRAL
}

/**
 * Split a coding into the {CPT, LOINC} dual columns the mockup expects.
 * Since the form schema carries a single (system, code) pair per order, the
 * code lands in either the CPT column or the LOINC column depending on its
 * system URI; the other column shows an em dash.
 */
function splitOrderCodes(order: ServiceRequestItem): { cpt: string; loinc: string } {
  if (!order.code) return { cpt: '', loinc: '' }
  if (order.system === CPT_SYSTEM) return { cpt: order.code, loinc: '' }
  if (order.system === LOINC_SYSTEM) return { cpt: '', loinc: order.code }
  // Unknown system — show the raw code under the CPT column (most common case
  // for picker-sourced orders) but don't claim it's CPT in the LOINC slot.
  return { cpt: order.code, loinc: '' }
}

export interface PlanSectionProps {
  readOnly: boolean
  /** `DocumentReference.docStatus`. Drives the Orders lifecycle pill so we
   *  don't claim an order is "Ordered" while the note is still preliminary
   *  (no lab routing has fired yet). Undefined → treat as preliminary. */
  docStatus?: 'preliminary' | 'final'
  serviceRequestsField: UseFieldArrayReturn<ProgressNoteEditFormValues, 'serviceRequests'>
  medicationsField: UseFieldArrayReturn<ProgressNoteEditFormValues, 'medications'>
  /**
   * When `true`, the inline `<BillingSummary>` lets the doctor change the visit's
   * billing (select a candidate, open the editor). In the edit form the parent
   * passes `!readOnly`; the locked viewer passes `true` at every Claim status.
   */
  billingEditable?: boolean
  /** The visit the billing summary reads and writes; without it the summary is not shown. */
  encounterId?: string
  /** Passed to `<BillingSummary>`: awaited before its editor opens; `false` keeps it closed. */
  beforeBillingEditorOpen?: () => Promise<unknown>
}

export function PlanSection({
  readOnly,
  docStatus,
  serviceRequestsField,
  medicationsField,
  billingEditable,
  encounterId,
  beforeBillingEditorOpen,
}: PlanSectionProps) {
  const { control, setValue } = useFormContext<ProgressNoteEditFormValues>()
  const navigate = useNavigate()
  const location = useLocation()
  const { placeOrderV2 } = useFlags()
  // "Ready to Place" pills deep-link to the Place Order page (PD-1212), gated
  // like the chart's Place Order button.
  const orderTestsPath = orderTestsPathFor(location.pathname)
  const placeOrderPath = placeOrderV2 ? orderTestsPath : null
  const orders = useWatch({ control, name: 'serviceRequests' }) || []
  const meds = useWatch({ control, name: 'medications' }) || []

  // PD-1600 — a low-confidence AI medication row: the pill names what the AI was
  // unsure of; confirming clears it as-is, editing the name clears it too.
  const renderMedicationFlag = (m: MedicationItem, idx: number) => {
    if (!m.needsReview) return null
    return (
      <Group gap={6} mt={4} wrap="nowrap" align="center">
        <Tooltip label={m.reviewReason ?? LOW_CONFIDENCE_REASON} multiline w={240} withArrow>
          <Badge size="xs" color="red" variant="light" style={{ cursor: 'default' }}>
            Needs review
          </Badge>
        </Tooltip>
        {!readOnly && (
          <Button
            size="compact-xs"
            variant="subtle"
            color="red"
            onClick={() => setValue(`medications.${idx}.needsReview`, false, { shouldDirty: true })}
          >
            Confirm
          </Button>
        )}
      </Group>
    )
  }
  const planNarrative = useWatch({ control, name: 'summary.plan' }) ?? ''

  // Hide an empty plan in read mode (faithfulness); always editable.
  const showPlanNarrative = !readOnly || planNarrative.trim() !== ''

  // Single org-scoped CPT load shared by every order row's inline picker.
  // The hook itself memoizes the result so this is a one-shot fetch even
  // across re-renders.
  const { options: procedureOptions, isLoading: procedureOptionsLoading } = useProcedureOptions()

  // Convert the org-CPT options to the `InlineCodeOption` shape the inline
  // picker expects (`{code, display, system}`). The `ProcedureOption`
  // already carries everything we need — we just drop the cached Mantine
  // `value`/`label` fields.
  const cptInlineOptions = useMemo<InlineCodeOption[]>(
    () =>
      procedureOptions.map((o) => ({
        code: o.code,
        display: o.display,
        system: o.coding.system || CPT_SYSTEM,
      })),
    [procedureOptions],
  )

  const showOrders = !readOnly || orders.length > 0
  const showMeds = !readOnly || meds.length > 0

  return (
    <Box mb="xl">
      <NoteHeading level="section" id="plan" label="Plan" />

      <Stack gap="lg">
        {showPlanNarrative && (
          <Box>
            <NoteHeading level="sub" id="plan-narrative" label="Plan Narrative" />
            {readOnly ? (
              <Text size="sm" c="dark.7" style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {planNarrative}
              </Text>
            ) : (
              <Controller
                control={control}
                name="summary.plan"
                render={({ field }) => (
                  <Textarea
                    autosize
                    minRows={3}
                    placeholder="Plan, follow-up timing, and return precautions"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.currentTarget.value)}
                    onBlur={field.onBlur}
                    data-testid="plan-narrative"
                    rightSectionPointerEvents="all"
                    rightSection={
                      <DictateFieldMic
                        field={{ path: 'summary.plan', label: 'Plan' }}
                        testId="plan-narrative"
                      />
                    }
                  />
                )}
              />
            )}
          </Box>
        )}

        {/* Orders */}
        {showOrders && (
          <Box>
            <NoteHeading level="sub" id="orders" label="Orders / Service Requests">
              <Text component="span" c="dimmed" size="xs">
                LOINC 47519-4
              </Text>
            </NoteHeading>
            {orders.length > 0 && (
              <Table withTableBorder striped="even" verticalSpacing="xs" highlightOnHover>
                <Table.Thead style={{ background: '#f9fafb' }}>
                  <Table.Tr>
                    <Table.Th style={{ color: '#374151', fontWeight: 700 }}>Order</Table.Th>
                    <Table.Th style={{ width: 110, color: '#374151', fontWeight: 700 }}>
                      CPT
                    </Table.Th>
                    <Table.Th style={{ width: 110, color: '#374151', fontWeight: 700 }}>
                      LOINC
                    </Table.Th>
                    {/* 160 fits "Ready to Place" in the closed Select without truncation */}
                    <Table.Th style={{ width: 160, color: '#374151', fontWeight: 700 }}>
                      Intent
                    </Table.Th>
                    <Table.Th style={{ width: 120, color: '#374151', fontWeight: 700 }}>
                      Priority
                    </Table.Th>
                    {!readOnly && <Table.Th style={{ width: 40 }} aria-label="actions" />}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {orders.map((order, idx) => {
                    const { cpt, loinc } = splitOrderCodes(order)
                    const statusColors = getOrderPillColors(order.intent, docStatus)
                    const statusLabel = getOrderPillLabel(order.intent)
                    const priority = order.priority ?? 'routine'
                    const priorityColors = PRIORITY_COLORS[priority]
                    return (
                      <Table.Tr key={order.id} data-testid={`plan-order-${order.code || order.id}`}>
                        {/* Col 1 — Order (description). Free-text input
                            that the CPT picker auto-fills when it picks an
                            empty row, but stays under doctor control once
                            edited. Matches the mockup's `Order` column. */}
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          {readOnly ? (
                            <Text size="sm" fw={500} c="dark.9">
                              {order.display || '—'}
                            </Text>
                          ) : (
                            <TextInput
                              size="xs"
                              value={order.display ?? ''}
                              onChange={(e) =>
                                setValue(`serviceRequests.${idx}.display`, e.currentTarget.value, {
                                  shouldDirty: true,
                                })
                              }
                              placeholder="Order description"
                            />
                          )}
                        </Table.Td>
                        {/* Col 2 — CPT code. Inline picker shows just the
                            code in coral monospace; dropdown items show
                            `code  description` filtered against the
                            org-scoped CPT options. Picks write both code
                            + system, and auto-fill the Order display in
                            col 1 when that field is empty. */}
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          {readOnly ? (
                            <Text size="xs" ff={MONO_FONT} c="dark.7">
                              {cpt || '—'}
                            </Text>
                          ) : (
                            <InlineCodePicker
                              value={
                                order.system === CPT_SYSTEM && order.code
                                  ? {
                                      code: order.code,
                                      display: order.display,
                                      system: order.system,
                                    }
                                  : undefined
                              }
                              options={cptInlineOptions}
                              loading={procedureOptionsLoading}
                              placeholder="CPT..."
                              onChange={(picked) => {
                                setValue(`serviceRequests.${idx}.system`, picked.system, {
                                  shouldDirty: true,
                                })
                                setValue(`serviceRequests.${idx}.code`, picked.code, {
                                  shouldDirty: true,
                                })
                                const existingDisplay = order.display?.trim() ?? ''
                                if (!existingDisplay || existingDisplay === order.code) {
                                  setValue(`serviceRequests.${idx}.display`, picked.display, {
                                    shouldDirty: true,
                                  })
                                }
                              }}
                              testId={`plan-order-cpt-${idx}`}
                            />
                          )}
                        </Table.Td>
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          {readOnly ? (
                            <Text size="xs" ff={MONO_FONT} c="dark.7">
                              {loinc || '—'}
                            </Text>
                          ) : (
                            <TextInput
                              size="xs"
                              value={order.system === LOINC_SYSTEM ? (order.code ?? '') : ''}
                              onChange={(e) => {
                                const next = e.currentTarget.value.trim()
                                if (!next) {
                                  // Clearing the LOINC text wipes the code only
                                  // when this row was a LOINC row to begin with;
                                  // CPT-coded rows are left alone.
                                  if (order.system === LOINC_SYSTEM) {
                                    setValue(`serviceRequests.${idx}.code`, '', {
                                      shouldDirty: true,
                                    })
                                  }
                                  return
                                }
                                setValue(`serviceRequests.${idx}.system`, LOINC_SYSTEM, {
                                  shouldDirty: true,
                                })
                                setValue(`serviceRequests.${idx}.code`, next, {
                                  shouldDirty: true,
                                })
                              }}
                              placeholder="LOINC"
                            />
                          )}
                        </Table.Td>
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          {readOnly ? (
                            <Badge
                              radius="xl"
                              size="sm"
                              variant="light"
                              // Ready-to-Place pills open the Place Order page
                              // (flag-gated, PD-1212); other intents stay static.
                              {...(order.intent === 'order' && placeOrderPath
                                ? {
                                    component: 'button' as const,
                                    type: 'button' as const,
                                    onClick: () => void navigate(placeOrderPath),
                                    title: 'Open Place Order',
                                  }
                                : {})}
                              styles={{
                                root: {
                                  background: statusColors.bg,
                                  color: statusColors.fg,
                                  fontWeight: 700,
                                  textTransform: 'none',
                                  ...(order.intent === 'order' && placeOrderPath
                                    ? { cursor: 'pointer' }
                                    : {}),
                                },
                              }}
                            >
                              {statusLabel}
                            </Badge>
                          ) : (
                            <Select
                              size="xs"
                              data={
                                ORDER_INTENT_OPTIONS as unknown as {
                                  value: string
                                  label: string
                                }[]
                              }
                              value={order.intent}
                              onChange={(value) => {
                                if (!value) return
                                setValue(
                                  `serviceRequests.${idx}.intent`,
                                  value as 'order' | 'plan' | 'proposal',
                                  { shouldDirty: true },
                                )
                              }}
                              // Popover wider than the narrow Intent column so "Ready to Place" doesn't wrap
                              comboboxProps={{ width: 230, position: 'bottom-start' }}
                            />
                          )}
                        </Table.Td>
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          {readOnly ? (
                            <Badge
                              radius="xl"
                              size="sm"
                              variant="light"
                              styles={{
                                root: {
                                  background: priorityColors.bg,
                                  color: priorityColors.fg,
                                  fontWeight: 700,
                                  textTransform: 'none',
                                },
                              }}
                            >
                              {ORDER_PRIORITY_OPTIONS.find((o) => o.value === priority)?.label ||
                                priority}
                            </Badge>
                          ) : (
                            <Select
                              size="xs"
                              data={
                                ORDER_PRIORITY_OPTIONS as unknown as {
                                  value: string
                                  label: string
                                }[]
                              }
                              value={priority}
                              onChange={(value) => {
                                if (!value) return
                                setValue(
                                  `serviceRequests.${idx}.priority`,
                                  value as 'routine' | 'urgent' | 'asap' | 'stat',
                                  { shouldDirty: true },
                                )
                              }}
                            />
                          )}
                        </Table.Td>
                        {!readOnly && (
                          <Table.Td style={{ verticalAlign: 'top' }}>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              onClick={() => serviceRequestsField.remove(idx)}
                              aria-label="Remove order"
                            >
                              <IconX size={14} />
                            </ActionIcon>
                          </Table.Td>
                        )}
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            )}
            {!readOnly && (
              <Button
                size="xs"
                variant="light"
                color="blue"
                mt="sm"
                leftSection={<IconPlus size={14} />}
                onClick={() =>
                  serviceRequestsField.append({
                    id: crypto.randomUUID(),
                    system: '',
                    code: '',
                    display: '',
                    category: 'laboratory',
                    intent: 'order',
                    priority: 'routine',
                    reasonText: null,
                  })
                }
                data-testid="plan-add-order"
              >
                Add Order
              </Button>
            )}
          </Box>
        )}

        {/* Medications */}
        {showMeds && (
          <Box>
            <NoteHeading level="sub" id="medications" label="Medications">
              <Text component="span" c="dimmed" size="xs">
                LOINC 10160-0
              </Text>
            </NoteHeading>
            {meds.length > 0 && (
              <Table withTableBorder striped="even" verticalSpacing="xs" highlightOnHover>
                <Table.Thead style={{ background: '#f9fafb' }}>
                  <Table.Tr>
                    <Table.Th style={{ color: '#374151', fontWeight: 700 }}>Medication</Table.Th>
                    <Table.Th style={{ width: 110, color: '#374151', fontWeight: 700 }}>
                      Dose
                    </Table.Th>
                    <Table.Th style={{ width: 90, color: '#374151', fontWeight: 700 }}>
                      Route
                    </Table.Th>
                    <Table.Th style={{ width: 120, color: '#374151', fontWeight: 700 }}>
                      Frequency
                    </Table.Th>
                    <Table.Th style={{ width: 130, color: '#374151', fontWeight: 700 }}>
                      Status
                    </Table.Th>
                    <Table.Th style={{ color: '#374151', fontWeight: 700 }}>Note</Table.Th>
                    {!readOnly && <Table.Th style={{ width: 40 }} aria-label="actions" />}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {meds.map((m, idx) => {
                    const statusColors = medStatusColors(m.status)
                    return (
                      <Table.Tr key={m.id} data-testid={`plan-med-${m.code || m.id}`}>
                        {/* Col 1 — Medication name. Live RxNorm typeahead via
                            `<RxNormMedicationPicker>` (uses the same ValueSet
                            as the side-menu Medications form —
                            `2.16.840.1.113762.1.4.1010.4`). Free-text
                            fallback if no match: the doctor's typed display
                            is preserved and the cascade creates a
                            display-only MedicationStatement (no RxNorm
                            code). When a pick happens, both code and display
                            are written to the row, and the RxNorm hint
                            line shows the cached code. */}
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          {readOnly ? (
                            <Stack gap={0}>
                              <Text size="sm" fw={600} c="dark.9">
                                {m.display || m.code || '—'}
                              </Text>
                              {m.code && (
                                <Text size="xs" c="dimmed">
                                  RxNorm {m.code}
                                </Text>
                              )}
                              {renderMedicationFlag(m, idx)}
                            </Stack>
                          ) : (
                            <Stack gap={2}>
                              <RxNormMedicationPicker
                                display={m.display ?? ''}
                                onDisplayChange={(next) => {
                                  setValue(`medications.${idx}.display`, next, {
                                    shouldDirty: true,
                                  })
                                  // Editing a flagged row counts as reviewing it.
                                  if (m.needsReview) {
                                    setValue(`medications.${idx}.needsReview`, false, {
                                      shouldDirty: true,
                                    })
                                  }
                                }}
                                onPick={(picked) => {
                                  setValue(`medications.${idx}.code`, picked.code, {
                                    shouldDirty: true,
                                  })
                                  setValue(`medications.${idx}.display`, picked.display, {
                                    shouldDirty: true,
                                  })
                                  // Heuristic prefill — fills `dose` from the
                                  // RxNorm strength (e.g. "325 MG" out of
                                  // "Acetaminophen 325 MG Oral Tablet") and
                                  // `route` from the dose form (Oral → PO).
                                  // We only write to a field when it's
                                  // currently empty so the doctor's manual
                                  // edits (or an AI-prefilled dose like
                                  // "2 tablets") aren't clobbered by picking
                                  // a different RxNorm to refine the name.
                                  // Frequency + status are intentionally
                                  // left untouched — those are clinical
                                  // decisions, not RxNorm properties.
                                  const inferredDose = extractDoseFromRxNorm(picked.display)
                                  if (inferredDose && !m.dose) {
                                    setValue(`medications.${idx}.dose`, inferredDose, {
                                      shouldDirty: true,
                                    })
                                  }
                                  const inferredRoute = extractRouteFromRxNorm(picked.display)
                                  if (inferredRoute && !m.route) {
                                    setValue(`medications.${idx}.route`, inferredRoute, {
                                      shouldDirty: true,
                                    })
                                  }
                                }}
                                placeholder="Search RxNorm…"
                                testId={`plan-med-name-${idx}`}
                              />
                              {m.code && (
                                <Text size="xs" c="dimmed" pl={4}>
                                  RxNorm {m.code}
                                </Text>
                              )}
                              {renderMedicationFlag(m, idx)}
                            </Stack>
                          )}
                        </Table.Td>
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          {readOnly ? (
                            <Text size="sm" c="dark.7">
                              {m.dose || '—'}
                            </Text>
                          ) : (
                            <TextInput
                              size="xs"
                              value={m.dose ?? ''}
                              onChange={(e) =>
                                setValue(`medications.${idx}.dose`, e.currentTarget.value || null, {
                                  shouldDirty: true,
                                })
                              }
                            />
                          )}
                        </Table.Td>
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          {readOnly ? (
                            <Text size="sm" c="dark.7">
                              {m.route
                                ? (ROUTE_OPTIONS.find((o) => o.value === m.route)?.label ?? m.route)
                                : '—'}
                            </Text>
                          ) : (
                            // Constrained list of common pharmacy routes
                            // (PO, SL, IV, IM, SC, Topical, etc.). `searchable`
                            // so the doctor can type "topi" or "in" to filter,
                            // `clearable` so picking back to "no route" is
                            // possible. Preselected automatically from the
                            // RxNorm display in the picker's `onPick`.
                            <Select
                              size="xs"
                              data={ROUTE_OPTIONS}
                              value={m.route ?? null}
                              onChange={(value) =>
                                setValue(`medications.${idx}.route`, value || null, {
                                  shouldDirty: true,
                                })
                              }
                              placeholder="Route…"
                              searchable
                              clearable
                              nothingFoundMessage="No matching route"
                              // The Route column is narrow; let the dropdown popover
                              // be wider than the input so the full labels
                              // ("PO — by mouth", "IM — intramuscular") render on a
                              // single line instead of wrapping.
                              comboboxProps={{ width: 230, position: 'bottom-start' }}
                            />
                          )}
                        </Table.Td>
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          {readOnly ? (
                            <Text size="sm" c="dark.7">
                              {m.frequency || '—'}
                            </Text>
                          ) : (
                            <TextInput
                              size="xs"
                              value={m.frequency ?? ''}
                              onChange={(e) =>
                                setValue(
                                  `medications.${idx}.frequency`,
                                  e.currentTarget.value || null,
                                  { shouldDirty: true },
                                )
                              }
                            />
                          )}
                        </Table.Td>
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          {readOnly ? (
                            <Badge
                              radius="xl"
                              size="sm"
                              variant="light"
                              styles={{
                                root: {
                                  background: statusColors.bg,
                                  color: statusColors.fg,
                                  fontWeight: 700,
                                  textTransform: 'none',
                                },
                              }}
                            >
                              {MED_STATUS_OPTIONS.find((o) => o.value === m.status)?.label ||
                                m.status}
                            </Badge>
                          ) : (
                            <Select
                              size="xs"
                              data={
                                MED_STATUS_OPTIONS as unknown as {
                                  value: string
                                  label: string
                                }[]
                              }
                              value={m.status}
                              onChange={(value) => {
                                if (!value) return
                                setValue(
                                  `medications.${idx}.status`,
                                  value as
                                    | 'active'
                                    | 'completed'
                                    | 'intended'
                                    | 'stopped'
                                    | 'on-hold',
                                  { shouldDirty: true },
                                )
                              }}
                            />
                          )}
                        </Table.Td>
                        <Table.Td style={{ verticalAlign: 'top' }}>
                          {readOnly ? (
                            <Text size="xs" c="dimmed">
                              {m.note || '—'}
                            </Text>
                          ) : (
                            <TextInput
                              size="xs"
                              value={m.note ?? ''}
                              onChange={(e) =>
                                setValue(`medications.${idx}.note`, e.currentTarget.value || null, {
                                  shouldDirty: true,
                                })
                              }
                            />
                          )}
                        </Table.Td>
                        {!readOnly && (
                          <Table.Td style={{ verticalAlign: 'top' }}>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              onClick={() => medicationsField.remove(idx)}
                              aria-label="Remove medication"
                            >
                              <IconX size={14} />
                            </ActionIcon>
                          </Table.Td>
                        )}
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            )}
            {!readOnly && (
              <Button
                size="xs"
                variant="light"
                color="blue"
                mt="sm"
                leftSection={<IconPlus size={14} />}
                onClick={() =>
                  medicationsField.append({
                    id: crypto.randomUUID(),
                    code: '',
                    display: '',
                    status: 'active',
                    intent: 'new-prescription',
                    dose: null,
                    route: null,
                    frequency: null,
                    note: null,
                  })
                }
                data-testid="plan-add-medication"
              >
                Add Medication
              </Button>
            )}
          </Box>
        )}

        {/* Billing / Coding Summary — mockup parity tail of the Plan section. It
            reads and writes the visit's billing itself; the host names the
            encounter and may flush its own save before the editor opens. */}
        {encounterId && (
          <BillingSummary
            encounterId={encounterId}
            editable={billingEditable ?? !readOnly}
            beforeEditorOpen={beforeBillingEditorOpen}
          />
        )}
      </Stack>
    </Box>
  )
}
